import * as React from 'react'
import { useCallback, useMemo } from 'react'

import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import setLanguageCookie from 'cookies/setLanguageCookie'
import UrlLocation from 'src/lib/url/UrlLocation'
import { getVisibleLanguageOptions } from 'i18n/util/locale'
import { LabRemoteConfigKeys } from 'constants/lab'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'

interface Props {
  onLanguageSelect?: React.MouseEventHandler<HTMLButtonElement>
}

function NavBarLanguageItems(props: Props) {
  const { onLanguageSelect } = props
  const locale = useAppSelector((state) => state.geo.locale)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(() => {
    return getVisibleLanguageOptions(languagesRemoteConfig)
  }, [languagesRemoteConfig])

  const currentLanguage =
    languageOptions.find((lang) => lang.locale === locale) ??
    languageOptions[0]

  const onLanguageClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      const selectedLocale = e.currentTarget.dataset.locale
      const selectedLanguage = languageOptions.find(
        (language) => language.locale === selectedLocale,
      )
      if (selectedLanguage) {
        setLanguageCookie(selectedLanguage.locale)
      }

      if (
        selectedLanguage &&
        selectedLanguage.locale !== currentLanguage?.locale
      ) {
        // no spoof check here in case customer calls to complain about seeing
        // another lang then CS can set back to english through this UI
        if (isLoggedIn) {
          updateCustomerLanguagePreferenceFromAuth(
            selectedLanguage.locale,
          ).catch(reportClientError)
        }

        const url = new URL(window.location.href)
        url.searchParams.delete('lang')
        const currentLocationUrl = new UrlLocation(url.pathname, url.search, url.hash)
        const redirect = currentLocationUrl.getRedirectToLocale(
          selectedLanguage.locale,
          languageOptions,
        )
        const targetUrl = currentLocationUrl.withRedirect(redirect)
        if (!targetUrl.equals(currentLocationUrl)) {
          window.location.href = targetUrl.toString()
        }
      }
      onLanguageSelect?.(e)
    },
    [currentLanguage?.locale, isLoggedIn, languageOptions, onLanguageSelect],
  )

  return (
    <>
      {languageOptions.map((language) => (
        <ClickableListItem
          size="medium"
          key={language.locale}
          endIcon={
            currentLanguage?.locale === language.locale ? (
              <LineCheckIcon />
            ) : undefined
          }
          title={language.nativeName}
          data-locale={language.locale}
          onClick={onLanguageClick}
          selected={currentLanguage?.locale === language.locale}
        />
      ))}
    </>
  )
}

export default React.memo(NavBarLanguageItems)
